import { useCallback, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';

/**
 * Keeps the active tab in the URL query string so browser back/forward
 * restores the correct section.
 */
export function useSyncedTab(defaultTab, paramName = 'tab') {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const activeTab = useMemo(() => {
    const fromUrl = searchParams.get(paramName);
    if (fromUrl) return fromUrl;
    if (location.state?.activeTab) return location.state.activeTab;
    return defaultTab;
  }, [searchParams, location.state?.activeTab, defaultTab, paramName]);

  const setActiveTab = useCallback((tab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!tab || tab === defaultTab) {
        next.delete(paramName);
      } else {
        next.set(paramName, tab);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams, defaultTab, paramName]);

  return [activeTab, setActiveTab];
}
